import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../Api/apiConfig";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { formatDate } from "../utils/date";
import { Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import "../styles/GrievancesTable.css";
import { useAuth } from "../lib/auth";

ChartJS.register(ArcElement, Tooltip, Legend);

const columnHelper = createColumnHelper();

// Mapping des statuts tel que défini dans votre table
const statutOptions = {
  new: "Nouveau",
  "en cours": "En cours",
  résolu: "Résolu",
  fermé: "Fermé",
};

export default function AllGrievancesPage() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [themes, setThemes] = useState([]);
  const [landlords, setLandlords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedTheme, setSelectedTheme] = useState("");
  const [selectedLandlord, setSelectedLandlord] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");

  const [showDetailsPopup, setShowDetailsPopup] = useState(false);
  const [selectedGrievance, setSelectedGrievance] = useState(null);

  // Édition / suppression
  const [isEditing, setIsEditing] = useState(false);
  const [editValues, setEditValues] = useState({
    title: "",
    description: "",
    statuts: "new",
  });
  const [isSaving, setIsSaving] = useState(false);

  const isAdmin = user?.role === "admin";

  // Récupération des données depuis l'API
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [grievancesResponse, themesResponse, landlordsResponse] =
          await Promise.all([
            apiClient.get("/grievances"),
            apiClient.get("/themes"),
            apiClient.get("/landlords"),
          ]);

        const themesData = Array.isArray(themesResponse.data)
          ? themesResponse.data
          : themesResponse.data.themes || [];

        const landlordsData = Array.isArray(landlordsResponse.data)
          ? landlordsResponse.data
          : landlordsResponse.data.landlords || [];

        const grievancesData = Array.isArray(grievancesResponse.data)
          ? grievancesResponse.data
          : grievancesResponse.data.grievances || [];

        // Création de mapping pour les thèmes et bailleurs
        const themesMap = themesData.reduce((acc, theme) => {
          acc[theme.id] = theme.name;
          return acc;
        }, {});

        const landlordsMap = landlordsData.reduce((acc, landlord) => {
          acc[landlord.id] = landlord.name;
          return acc;
        }, {});

        // Enrichissement des doléances avec les noms de thème et bailleur
        const grievancesWithDetails = grievancesData.map((grievance) => ({
          ...grievance,
          theme_name: themesMap[grievance.theme_id] || "Non défini",
          landlord_name:
            landlordsMap[grievance.landlord_id] || "Non défini",
        }));

        setData(grievancesWithDetails);
        setThemes(themesData);
        setLandlords(landlordsData);
      } catch (err) {
        console.error(err);
        setError("Erreur lors de la récupération des données.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Ouverture / fermeture de la popup des détails
  const openDetailsPopup = (grievance) => {
    setSelectedGrievance(grievance);
    setEditValues({
      title: grievance.title || "",
      description: grievance.description || "",
      statuts: grievance.statuts?.trim() || "new",
    });
    setIsEditing(false);
    setShowDetailsPopup(true);
  };

  const closeDetailsPopup = () => {
    setShowDetailsPopup(false);
    setSelectedGrievance(null);
    setIsEditing(false);
  };

  // Filtrage des doléances selon les sélections
  const filteredData = useMemo(() => {
    return data.filter((grievance) => {
      const matchesTheme =
        !selectedTheme || grievance.theme_name === selectedTheme;
      const matchesLandlord =
        !selectedLandlord ||
        grievance.landlord_name === selectedLandlord;
      const matchesStatus =
        !selectedStatus ||
        grievance.statuts.trim() === selectedStatus;

      return matchesTheme && matchesLandlord && matchesStatus;
    });
  }, [data, selectedTheme, selectedLandlord, selectedStatus]);

  // Préparation des données pour le graphique par statut
  const statusCounts = useMemo(() => {
    const counts = { new: 0, "en cours": 0, résolu: 0, fermé: 0 };
    data.forEach((grievance) => {
      const status = grievance.statuts.trim();
      if (counts.hasOwnProperty(status)) {
        counts[status]++;
      }
    });
    return counts;
  }, [data]);

  const pieDataStatus = {
    labels: Object.keys(statusCounts).map((key) => statutOptions[key]),
    datasets: [
      {
        data: Object.values(statusCounts),
        backgroundColor: ["#36A2EB", "#FFCE56", "#4BC0C0", "#FF6384"],
      },
    ],
  };

  // Préparation des données pour le graphique par bailleur
  const landlordCounts = useMemo(() => {
    const counts = {};
    data.forEach((grievance) => {
      const landlord = grievance.landlord_name;
      counts[landlord] = (counts[landlord] || 0) + 1;
    });
    return counts;
  }, [data]);

  const pieDataLandlords = {
    labels: Object.keys(landlordCounts),
    datasets: [
      {
        data: Object.values(landlordCounts),
        backgroundColor: [
          "#FF6384",
          "#36A2EB",
          "#FFCE56",
          "#4BC0C0",
          "#9966FF",
          "#FF9F40",
          "#8AC926",
          "#1982C4",
        ],
      },
    ],
  };

  // Définition des colonnes du tableau
  const columns = useMemo(
    () => [
      columnHelper.accessor("reference", {
        header: "Référence",
        cell: (info) => info.getValue() || "N/A",
      }),
      columnHelper.accessor("title", {
        header: "Titre",
        cell: (info) => info.getValue() || "Sans titre",
      }),
      columnHelper.accessor("theme_name", {
        header: "Thème",
        cell: (info) => info.getValue() || "Non défini",
      }),
      columnHelper.accessor("landlord_name", {
        header: "Bailleur",
        cell: (info) => info.getValue() || "Non défini",
      }),
      columnHelper.accessor("statuts", {
        header: "Statut",
        cell: (info) => {
          const statusValue = info.getValue().trim();
          return statutOptions[statusValue] || statusValue;
        },
      }),
      columnHelper.accessor("created_at", {
        header: "Date",
        cell: (info) => formatDate(info.getValue()),
      }),
      columnHelper.display({
        id: "actions",
        header: "Actions",
        cell: (info) => (
          <button onClick={() => openDetailsPopup(info.row.original)}>
            Détails
          </button>
        ),
      }),
    ],
    []
  );

  const table = useReactTable({
    data: filteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // --- Droits : user peut modifier/supprimer SA doléance, admin toutes ---
  const canEditOrDelete = (grievance) => {
    if (!isAuthenticated || !user) return false;

    const isOwner =
      String(grievance.user_id) === String(user.id) ||
      String(grievance.userId) === String(user.id); // au cas où le champ s'appelle userId

    return isAdmin || isOwner;
  };

  // --- Handler suppression ---
  const handleDelete = async () => {
    if (!selectedGrievance) return;

    if (
      !canEditOrDelete(selectedGrievance) ||
      !window.confirm(
        "Êtes-vous sûr de vouloir supprimer cette doléance ? Cette action est irréversible."
      )
    ) {
      return;
    }

    try {
      await apiClient.delete(`/grievances/${selectedGrievance.id}`);

      setData((prev) =>
        prev.filter((g) => g.id !== selectedGrievance.id)
      );
      closeDetailsPopup();
    } catch (err) {
      console.error(err);
      setError(
        "Erreur lors de la suppression de la doléance. Merci de réessayer."
      );
    }
  };

  // --- Handler sauvegarde édition ---
  const handleSave = async () => {
    if (!selectedGrievance) return;
    if (!canEditOrDelete(selectedGrievance)) {
      alert("Vous n'avez pas les droits pour modifier cette doléance.");
      return;
    }

    if (!editValues.title.trim() || !editValues.description.trim()) {
      alert("Le titre et la description sont obligatoires.");
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        title: editValues.title.trim(),
        description: editValues.description.trim(),
        statuts: editValues.statuts,
      };

      const response = await apiClient.put(
        `/grievances/${selectedGrievance.id}`,
        payload
      );

      const updated = response.data || {
        ...selectedGrievance,
        ...payload,
      };

      setData((prev) =>
        prev.map((g) =>
          g.id === selectedGrievance.id ? { ...g, ...updated } : g
        )
      );

      setSelectedGrievance((prev) =>
        prev ? { ...prev, ...updated } : prev
      );

      setIsEditing(false);
    } catch (err) {
      console.error(err);
      setError(
        "Erreur lors de la mise à jour de la doléance. Merci de réessayer."
      );
    } finally {
      setIsSaving(false);
    }
  };

  // --- Bouton "Ajouter une doléance" ---
  const handleAddGrievance = () => {
    navigate("/grievances"); // réutilise ta page de création déjà existante
  };

  if (loading) return <p>Chargement des données...</p>;
  if (error) return <p className="error-message">{error}</p>;

  return (
    <div className="grievances-page">
      <div className="sidebar">
        <h2>Statuts</h2>
        <Pie data={pieDataStatus} />
        <h2 style={{ marginTop: "2rem" }}>
          Réclamations par bailleur
        </h2>
        <Pie data={pieDataLandlords} />
      </div>

      <div className="main-content">
        <div className="grievances-header-row">
          <h1>Toutes les Doléances</h1>
          <button
            className="add-grievance-button"
            onClick={handleAddGrievance}
          >
            + Ajouter une doléance
          </button>
        </div>

        {/* Filtres */}
        <div className="filters">
          <label>
            Thème :
            <select
              value={selectedTheme}
              onChange={(e) => setSelectedTheme(e.target.value)}
            >
              <option value="">Tous les thèmes</option>
              {themes.map((theme) => (
                <option key={theme.id} value={theme.name}>
                  {theme.name}
                </option>
              ))}
            </select>
          </label>

          <label>
            Bailleur :
            <select
              value={selectedLandlord}
              onChange={(e) =>
                setSelectedLandlord(e.target.value)
              }
            >
              <option value="">Tous les bailleurs</option>
              {landlords.map((landlord) => (
                <option key={landlord.id} value={landlord.name}>
                  {landlord.name}
                </option>
              ))}
            </select>
          </label>

          <label>
            Statut :
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              <option value="">Tous les statuts</option>
              <option value="new">Nouveau</option>
              <option value="en cours">En cours</option>
              <option value="résolu">Résolu</option>
              <option value="fermé">Fermé</option>
            </select>
          </label>
        </div>

        {/* Tableau des doléances */}
        <table>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id}>
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>
                    {flexRender(
                      cell.column.columnDef.cell,
                      cell.getContext()
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Popup des détails / édition */}
      {showDetailsPopup && selectedGrievance && (
        <div className="all-grievances-popup">
          <div className="all-grievances-popup-content">
            <h3>Détails de la Doléance</h3>

            {!isEditing ? (
              <>
                <p>
                  <strong>Référence :</strong>{" "}
                  {selectedGrievance.reference}
                </p>
                <p>
                  <strong>Titre :</strong>{" "}
                  {selectedGrievance.title}
                </p>
                <p>
                  <strong>Thème :</strong>{" "}
                  {selectedGrievance.theme_name}
                </p>
                <p>
                  <strong>Bailleur :</strong>{" "}
                  {selectedGrievance.landlord_name}
                </p>
                <p>
                  <strong>Statut :</strong>{" "}
                  {statutOptions[
                    selectedGrievance.statuts.trim()
                  ] || selectedGrievance.statuts}
                </p>
                <p>
                  <strong>Description :</strong>{" "}
                  {selectedGrievance.description}
                </p>
                <p>
                  <strong>Date :</strong>{" "}
                  {formatDate(selectedGrievance.created_at)}
                </p>
              </>
            ) : (
              <>
                <label>
                  Titre :
                  <input
                    type="text"
                    value={editValues.title}
                    onChange={(e) =>
                      setEditValues((prev) => ({
                        ...prev,
                        title: e.target.value,
                      }))
                    }
                  />
                </label>
                <label>
                  Description :
                  <textarea
                    rows={4}
                    value={editValues.description}
                    onChange={(e) =>
                      setEditValues((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                  />
                </label>
                <label>
                  Statut :
                  <select
                    value={editValues.statuts}
                    onChange={(e) =>
                      setEditValues((prev) => ({
                        ...prev,
                        statuts: e.target.value,
                      }))
                    }
                  >
                    <option value="new">Nouveau</option>
                    <option value="en cours">En cours</option>
                    <option value="résolu">Résolu</option>
                    <option value="fermé">Fermé</option>
                  </select>
                </label>
              </>
            )}

            <div className="all-grievances-popup-actions">
              <button onClick={closeDetailsPopup}>Fermer</button>

              {!isEditing && (
                <>
                  <button
                    onClick={() => setIsEditing(true)}
                    disabled={
                      !selectedGrievance ||
                      !canEditOrDelete(selectedGrievance)
                    }
                  >
                    Modifier
                  </button>
                  <button
                    className="danger"
                    onClick={handleDelete}
                    disabled={
                      !selectedGrievance ||
                      !canEditOrDelete(selectedGrievance)
                    }
                  >
                    Supprimer
                  </button>
                </>
              )}

              {isEditing && (
                <>
                  <button
                    onClick={handleSave}
                    disabled={
                      isSaving ||
                      !selectedGrievance ||
                      !canEditOrDelete(selectedGrievance)
                    }
                  >
                    {isSaving
                      ? "Enregistrement..."
                      : "Enregistrer"}
                  </button>
                  <button
                    onClick={() => setIsEditing(false)}
                  >
                    Annuler
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
